import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";

import { Context as PermissoesContext } from "../../../Context/PermissoesContext";
import { api } from "../../../services/Api";

function getPermissionId(permissao) {
  return String(permissao.id);
}

function getModuloPermissoes(modulo) {
  return modulo.permissoes || modulo.acoes || modulo.permissions || [];
}

function getModuloId(modulo, permissaoReferencia) {
  return modulo.id || modulo.id_modulo || permissaoReferencia?.id_modulo || permissaoReferencia?.modulo?.id;
}

function getModuloNome(modulo, permissaoReferencia) {
  return modulo.nome || modulo.name || permissaoReferencia?.modulo?.nome || "Módulo sem nome";
}

function getCodigoPermissaoModulo(modulo) {
  const primeiraPermissao = getModuloPermissoes(modulo)[0];
  return primeiraPermissao?.codigo?.replace("1", "0") || "";
}

function normalizarModulos(modulos = []) {
  if (!modulos.length) return [];

  if (modulos.some(modulo => getModuloPermissoes(modulo).length > 0)) {
    return modulos.map(modulo => ({
      ...modulo,
      id: getModuloId(modulo, getModuloPermissoes(modulo)[0]),
      nome: getModuloNome(modulo, getModuloPermissoes(modulo)[0]),
      permissoes: getModuloPermissoes(modulo).map(permissao => ({
        ...permissao,
        id_modulo: permissao.id_modulo || getModuloId(modulo, permissao),
      })),
    }));
  }

  const modulosPorId = new Map();
  modulos.forEach(permissao => {
    if (!permissao.codigo) return;
    const idModulo = getModuloId({}, permissao);
    const nomeModulo = getModuloNome({}, permissao);

    if (!modulosPorId.has(idModulo)) {
      modulosPorId.set(idModulo, {
        id: idModulo,
        nome: nomeModulo,
        permissoes: [],
      });
    }

    modulosPorId.get(idModulo).permissoes.push(permissao);
  });

  return Array.from(modulosPorId.values());
}

function setsIguais(setA, setB) {
  if (setA.size !== setB.size) return false;
  return Array.from(setA).every(item => setB.has(item));
}

function getPermissoesSelecionadasDoModulo(modulo, idsSelecionados) {
  return modulo.permissoes.filter(permissao => idsSelecionados.has(getPermissionId(permissao)));
}

function normalizarTexto(valor = "") {
  return String(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getTermosBusca(filtro) {
  return normalizarTexto(filtro).split(/\s+/).filter(Boolean);
}

function textoPermissao(permissao) {
  return normalizarTexto(`${permissao.nome || ""} ${permissao.codigo || ""}`);
}

function todosTermosCombinam(texto, termos) {
  return termos.every(termo => texto.includes(termo));
}

function ListaModulosSkeleton() {
  return (
    <div className="macro-skeleton-list" aria-label="Carregando módulos">
      {Array.from({ length: 8 }).map((_, index) => (
        <div className="macro-skeleton-item" key={`macro-skeleton-${index}`}>
          <Skeleton variant="text" width={`${32 + (index % 3) * 8}%`} height={28} />
          <div className="macro-skeleton-meta">
            <Skeleton variant="rounded" width={132} height={24} />
            <Skeleton variant="circular" width={24} height={24} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function VisaoGeralPermissoes({
  usuarioSelecionado,
  permissoesUsuario,
  setPermissoesUsuario,
  setLoading,
  enqueueSnackbar,
  onDirtyChange,
  onSaved,
}) {
  const { checarPermissaoSemLog } = useContext(PermissoesContext);
  const [modulos, setModulos] = useState([]);
  const [idsOriginais, setIdsOriginais] = useState(new Set());
  const [idsModificados, setIdsModificados] = useState(new Set());
  const [filtro, setFiltro] = useState("");
  const [loadingLocal, setLoadingLocal] = useState(false);

  const idsPermissoesUsuario = useMemo(
    () => new Set((permissoesUsuario || []).map(permissao => getPermissionId(permissao))),
    [permissoesUsuario]
  );

  useEffect(() => {
    setIdsOriginais(new Set(idsPermissoesUsuario));
    setIdsModificados(new Set(idsPermissoesUsuario));
  }, [idsPermissoesUsuario, usuarioSelecionado.id]);

  useEffect(() => {
    async function carregarCatalogo() {
      setLoading(true);
      setLoadingLocal(true);
      try {
        const {
          data: { modulos: modulosCatalogo },
        } = await api.get("/v2/controleAcesso/modulosEPermissoes");

        setModulos(normalizarModulos(modulosCatalogo));
      } catch (error) {
        enqueueSnackbar("Não foi possível carregar a visão geral de permissões.", {
          variant: "error",
          autoHideDuration: 3000,
        });
      } finally {
        setLoading(false);
        setLoadingLocal(false);
      }
    }

    carregarCatalogo();
  }, [enqueueSnackbar, setLoading]);

  const existemAlteracoes = useMemo(
    () => !setsIguais(idsOriginais, idsModificados),
    [idsOriginais, idsModificados]
  );

  useEffect(() => {
    onDirtyChange(existemAlteracoes);
  }, [existemAlteracoes, onDirtyChange]);

  function podeEditarModulo(modulo) {
    const codigoModulo = getCodigoPermissaoModulo(modulo);
    return checarPermissaoSemLog("CA002") || (codigoModulo && checarPermissaoSemLog(codigoModulo));
  }

  function avisarSemPermissao(modulo) {
    const codigoModulo = getCodigoPermissaoModulo(modulo);
    enqueueSnackbar(`Você não tem permissão para alterar este módulo. Código: ${codigoModulo}`, {
      variant: "error",
      autoHideDuration: 3000,
    });
  }

  function alternarPermissao(modulo, permissao) {
    if (!podeEditarModulo(modulo)) {
      avisarSemPermissao(modulo);
      return;
    }

    const idPermissao = getPermissionId(permissao);
    setIdsModificados(prevState => {
      const proximosIds = new Set(prevState);
      if (proximosIds.has(idPermissao)) {
        proximosIds.delete(idPermissao);
      } else {
        proximosIds.add(idPermissao);
      }
      return proximosIds;
    });
  }

  function marcarModulo(modulo, marcar) {
    if (!podeEditarModulo(modulo)) {
      avisarSemPermissao(modulo);
      return;
    }

    setIdsModificados(prevState => {
      const proximosIds = new Set(prevState);
      modulo.permissoes.forEach(permissao => {
        const idPermissao = getPermissionId(permissao);
        if (marcar) {
          proximosIds.add(idPermissao);
        } else {
          proximosIds.delete(idPermissao);
        }
      });
      return proximosIds;
    });
  }

  function moduloAlterado(modulo) {
    const idsModulo = new Set(modulo.permissoes.map(permissao => getPermissionId(permissao)));
    return Array.from(idsModulo).some(
      idPermissao => idsOriginais.has(idPermissao) !== idsModificados.has(idPermissao)
    );
  }

  async function salvarAlteracoes() {
    const modulosAlterados = modulos.filter(modulo => moduloAlterado(modulo));
    if (!modulosAlterados.length) return;

    setLoading(true);
    setLoadingLocal(true);
    try {
      await Promise.all(
        modulosAlterados.map(modulo =>
          api.put(
            `/v2/controleAcesso/usuario/${usuarioSelecionado.id}/permissoes/${modulo.id}`,
            {
              permissoes: getPermissoesSelecionadasDoModulo(modulo, idsModificados),
            }
          )
        )
      );

      const permissoesSelecionadas = modulos.flatMap(modulo =>
        getPermissoesSelecionadasDoModulo(modulo, idsModificados)
      );

      setIdsOriginais(new Set(idsModificados));
      setPermissoesUsuario(permissoesSelecionadas);
      onSaved(permissoesSelecionadas);
      enqueueSnackbar("Alterações salvas com sucesso.", {
        variant: "success",
        autoHideDuration: 3000,
      });
    } catch (error) {
      enqueueSnackbar("Não foi possível salvar as alterações. Tente novamente.", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setLoading(false);
      setLoadingLocal(false);
    }
  }

  function cancelarAlteracoes() {
    setIdsModificados(new Set(idsOriginais));
  }

  const termosFiltro = useMemo(() => getTermosBusca(filtro), [filtro]);
  const modulosFiltrados = useMemo(() => {
    if (!termosFiltro.length) {
      return modulos
        .map(modulo => ({ ...modulo, permissoesVisiveis: modulo.permissoes }))
        .sort((a, b) => a.nome.toUpperCase().localeCompare(b.nome.toUpperCase()));
    }

    return modulos
      .map(modulo => {
        const nomeModuloNormalizado = normalizarTexto(modulo.nome);
        const moduloCombina = todosTermosCombinam(nomeModuloNormalizado, termosFiltro);
        const permissoesEncontradas = modulo.permissoes.filter(permissao =>
          todosTermosCombinam(textoPermissao(permissao), termosFiltro)
        );
        const permissoesVisiveis = moduloCombina ? modulo.permissoes : permissoesEncontradas;

        if (!permissoesVisiveis.length) return null;

        return {
          ...modulo,
          filtroModuloCombina: moduloCombina,
          permissoesEncontradas: permissoesEncontradas.length,
          permissoesVisiveis,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.filtroModuloCombina !== b.filtroModuloCombina) {
          return a.filtroModuloCombina ? -1 : 1;
        }

        const aComecaComFiltro = normalizarTexto(a.nome).startsWith(termosFiltro[0]);
        const bComecaComFiltro = normalizarTexto(b.nome).startsWith(termosFiltro[0]);
        if (aComecaComFiltro !== bComecaComFiltro) {
          return aComecaComFiltro ? -1 : 1;
        }

        return a.nome.toUpperCase().localeCompare(b.nome.toUpperCase());
      });
  }, [termosFiltro, modulos]);

  const filtroAtivo = termosFiltro.length > 0;

  return (
    <section className="macro-permissoes">
      <div className="macro-toolbar">
        <TextField
          className="macro-search"
          label="Buscar módulo, permissão ou código"
          size="small"
          value={filtro}
          onChange={event => setFiltro(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="macro-search-icon" fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: filtro && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Limpar busca"
                  edge="end"
                  size="small"
                  onClick={() => setFiltro("")}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          helperText={
            filtroAtivo
              ? `${modulosFiltrados.length} módulo(s) encontrado(s)`
              : " "
          }
        />
        {loadingLocal && (
          <span className="painel-loading">
            <CircularProgress size={18} />
            Carregando
          </span>
        )}
      </div>

      <div className="macro-lista">
        {loadingLocal && modulos.length === 0 && <ListaModulosSkeleton />}

        {!(loadingLocal && modulos.length === 0) && modulosFiltrados.map(modulo => {
          const totalPermissoes = modulo.permissoes.length;
          const totalAutorizadas = modulo.permissoes.filter(permissao =>
            idsModificados.has(getPermissionId(permissao))
          ).length;
          const alterado = moduloAlterado(modulo);
          const editaModulo = podeEditarModulo(modulo);

          return (
            <Accordion key={`${modulo.id}-${modulo.nome}`} className="macro-accordion">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <div className="macro-accordion-summary">
                  <strong>{modulo.nome.toUpperCase()}</strong>
                  <div className="macro-accordion-meta">
                    <Chip
                      size="small"
                      label={`${totalAutorizadas} de ${totalPermissoes} permissões`}
                    />
                    {alterado && <Chip size="small" color="warning" label="Alterado" />}
                    {!editaModulo && <Chip size="small" label="Somente leitura" />}
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <div className="macro-module-actions">
                  <Button
                    type="button"
                    size="small"
                    disabled={!editaModulo}
                    onClick={() => marcarModulo(modulo, true)}
                  >
                    Marcar todas
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    disabled={!editaModulo}
                    onClick={() => marcarModulo(modulo, false)}
                  >
                    Desmarcar todas
                  </Button>
                </div>

                <div className="macro-permissions-grid">
                  {modulo.permissoesVisiveis.map(permissao => {
                    const checked = idsModificados.has(getPermissionId(permissao));
                    return (
                      <div className="macro-permission-item" key={`macro-${permissao.id}`}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checked}
                              disabled={!editaModulo}
                              onChange={() => alternarPermissao(modulo, permissao)}
                            />
                          }
                          label={permissao.nome}
                        />
                        <span>{permissao.codigo}</span>
                      </div>
                    );
                  })}
                </div>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {!loadingLocal && !modulosFiltrados.length && (
          <div className="painel-module-empty">
            Nenhum módulo ou permissão encontrado para o filtro informado.
          </div>
        )}
      </div>

      <div className="painel-buttons-wrapper">
        <Button
          type="button"
          className="secondary"
          disabled={!existemAlteracoes}
          onClick={cancelarAlteracoes}
        >
          Cancelar alterações
        </Button>
        <Button type="button" disabled={!existemAlteracoes} onClick={salvarAlteracoes}>
          Salvar alterações
        </Button>
      </div>
    </section>
  );
}
